import { useNavigation } from "@/providers";
import { RobotoTooltip } from "@/shared/components";
import { HighlightedSpan } from "@/shared/components/ai/AISummaryMarkdownLink/HighlightedSpan.tsx";
import { useDataset } from "@/shared/domain/datasets/hooks";
import { IngestionStatus } from "@/shared/domain/files";
import { useFile } from "@/shared/domain/files/hooks";
import { useMessagePathById } from "@/shared/domain/topics/hooks/useMessagePath.ts";
import { useTopicById } from "@/shared/domain/topics/hooks/useTopic.ts";
import { getWorkspaceInput } from "@/shared/domain/workspaces/WorkspaceBuilder.ts";
import { determineDefaultPanelType } from "@/shared/state/visualization/schema/v1";

interface VizExternalLinkProps {
  datasetId?: string;
  fileId?: string;
  topicId?: string;
  messagePathId?: string;
  time?: bigint;
  text: string;
  onLinkClick?: () => void;
}

export const VizExternalLink = ({
  datasetId,
  fileId,
  topicId,
  messagePathId,
  time,
  text,
  onLinkClick,
}: VizExternalLinkProps) => {
  const { goto } = useNavigation();
  const dataset = useDataset(datasetId);
  const file = useFile(fileId);
  const topic = useTopicById(topicId);
  const messagePath = useMessagePathById(messagePathId);
  const messagePathTopic = useTopicById(messagePath.data?.topic_id);

  let linkProps:
    | {
        tooltipText: string;
        onClick: () => void;
      }
    | undefined = undefined;

  if (messagePath.data && messagePathTopic.data) {
    const panelType = determineDefaultPanelType(messagePath.data);
    linkProps = {
      tooltipText: `Visualize: ${messagePathTopic.data.topic_name}.${messagePath.data.message_path}`,
      onClick: () => {
        onLinkClick?.();
        goto.visualize({
          dataSource: {
            files: [messagePathTopic.data.association.association_id],
          },
          params: {
            layout: getWorkspaceInput(
              panelType,
              messagePathTopic.data,
              messagePath.data,
            ),
            t: time,
          },
        });
      },
    };
  } else if (topic.data) {
    linkProps = {
      tooltipText: `Visualize: ${topic.data.topic_name}`,
      onClick: () => {
        onLinkClick?.();
        goto.visualize({
          dataSource: {
            files: [topic.data.association.association_id],
          },
          params: {
            t: time,
          },
          state: {
            initialTopicFilter: {
              term: topic.data.topic_name,
              key: Date.now(), // Unique key to issue a new command
            },
          },
        });
      },
    };
  } else if (file.data) {
    linkProps = {
      tooltipText: `View: ${file.data.relative_path}`,
      onClick: () => {
        onLinkClick?.();
        if (file.data.ingestion_status !== IngestionStatus.NotIngested) {
          goto.visualize({
            dataSource: {
              files: [file.data.file_id],
            },
            params: {
              t: time,
            },
          });
        } else {
          goto.file({
            fileId: file.data.file_id,
          });
        }
      },
    };
  } else if (dataset.data) {
    linkProps = {
      tooltipText: `View: '${dataset.data.dataset_id}'`,
      onClick: () => {
        onLinkClick?.();
        goto.dataset({
          datasetId: dataset.data.dataset_id,
        });
      },
    };
  }

  // If the link doesn't resolve to anything valid, just render the text
  if (!linkProps) {
    return <span>{text}</span>;
  }

  // Regular navigation link (not in visualizer or dataset link). No hover
  // toolbar: the click already opens the target at `time`, and a toolbar over
  // the link would only cover its click target.
  return (
    <RobotoTooltip title={linkProps.tooltipText}>
      <HighlightedSpan onClick={linkProps.onClick}>{text}</HighlightedSpan>
    </RobotoTooltip>
  );
};
